import React, { PropsWithChildren, useCallback, useState } from 'react'
import { FormValues } from './all-transactions/index.context'

/**
 * Context to persist transaction filters between navigations
 * Allows users to return from results page with filters maintained
 */

const STORAGE_KEYS = {
  FILTERS: 'bfa_transaction_filters',
  SEARCH_PARAMS: 'bfa_transaction_search_params',
}

export interface TransactionFiltersContextProps {
  savedFilters: FormValues | null
  saveFilters: (filters: FormValues) => void
  clearFilters: () => void
  hasFilters: boolean
  savedSearchParams: any | null
  saveSearchParams: (params: any) => void
  restoreSearchParams: () => any | null
  clearSearchParams: () => void
  hasSearchParams: boolean
}

export const TransactionFiltersContext = React.createContext<TransactionFiltersContextProps>({
  savedFilters: null,
  saveFilters: () => {},
  clearFilters: () => {},
  hasFilters: false,
  savedSearchParams: null,
  saveSearchParams: () => {},
  restoreSearchParams: () => null,
  clearSearchParams: () => {},
  hasSearchParams: false,
})

export const useTransactionFilters = () => React.useContext(TransactionFiltersContext)

/**
 * Provider that keeps filters in memory during the session
 * Filters are automatically cleared when:
 * - Page is refreshed
 * - Reset button is clicked
 * - Application is closed/exited
 */
export const TransactionFiltersProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [savedFilters, setSavedFilters] = useState<FormValues | null>(null)

  const saveFilters = useCallback((filters: FormValues) => {
    setSavedFilters(filters)
    try {
      sessionStorage.setItem(STORAGE_KEYS.FILTERS, JSON.stringify(filters))
    } catch (error) {
      console.error('Error saving filters to sessionStorage:', error)
    }
  }, [])

  const clearFilters = useCallback(() => {
    setSavedFilters(null)
    try {
      sessionStorage.removeItem(STORAGE_KEYS.FILTERS)
    } catch (error) {
      console.error('Error clearing filters from sessionStorage:', error)
    }
  }, [])

  const saveSearchParams = useCallback((params: any) => {
    try {
      sessionStorage.setItem(STORAGE_KEYS.SEARCH_PARAMS, JSON.stringify(params))
    } catch (error) {
      console.error('Error saving search params to sessionStorage:', error)
    }
  }, [])

  const restoreSearchParams = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEYS.SEARCH_PARAMS)
      return stored ? JSON.parse(stored) : null
    } catch (error) {
      console.error('Error restoring search params from sessionStorage:', error)
      return null
    }
  }, [])

  const clearSearchParams = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEYS.SEARCH_PARAMS)
    } catch (error) {
      console.error('Error clearing search params from sessionStorage:', error)
    }
  }, [])

  const hasFilters = savedFilters !== null
  const hasSearchParams = !!restoreSearchParams()

  return (
    <TransactionFiltersContext.Provider
      value={{
        savedFilters,
        saveFilters,
        clearFilters,
        hasFilters,
        savedSearchParams: restoreSearchParams(),
        saveSearchParams,
        restoreSearchParams,
        clearSearchParams,
        hasSearchParams,
      }}
    >
      {children}
    </TransactionFiltersContext.Provider>
  )
}

